import { IDropdownOption } from '@fluentui/react';
import { Dropdown, FileUpload, useClassNames } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  FeatureSetSearchResults,
  ProjectSearchResults,
  TransformationType,
  VersionedId,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { TransformationInfo } from '../../featurestore/models';
import { getDropdownOptions, getMd5Checksum } from '../../featurestore/utils';
import {
  featureSetTransformationStylesDefault,
  IFeatureSetTransformationClassNames,
  IFeatureSetTransformationStyles,
} from './FeatureSetTransformation.styles';

interface IFeatureSetTransformationProps {
  transformationType?: TransformationType;
  onChange: (transformationInfo: TransformationInfo) => void;
}

export const FeatureSetTransformation = ({ transformationType, onChange }: IFeatureSetTransformationProps) => {
  const [values, setValues] = useState<TransformationInfo>({});
  const [projectOptions, setProjectOptions] = useState<IDropdownOption[]>();
  const [featureSetOptions, setFeatureSetOptions] = useState<IDropdownOption[]>();
  const loadProjects = useCallback(async () => {
    const { listableProject = [] }: ProjectSearchResults = await fsApi(CoreService.SearchProjects, {
      requiredPermission: ActivePermission.ACTIVE_PERMISSION_EDITOR,
      pageSize: 1000,
    });
    setProjectOptions(getDropdownOptions(listableProject));
  }, []);
  const loadFeatureSets = useCallback(
    async (projectId?: string) => {
      const { listableFeatureSet = [] }: FeatureSetSearchResults = await fsApi(CoreService.SearchFeatureSets, {
        projectIds: [projectId],
        pageSize: 1000,
      });
      setFeatureSetOptions(getDropdownOptions(listableFeatureSet));
    },
    [featureSetOptions]
  );
  const onChangeValues = useCallback((values: TransformationInfo) => {
    setValues(values);
    onChange(values);
  }, []);
  useEffect(() => {
    if (!projectOptions) {
      loadProjects();
    }
  }, []);
  useEffect(() => {
    setValues({ ...values, transformationType });
  }, [transformationType]);
  const classNames = useClassNames<IFeatureSetTransformationStyles, IFeatureSetTransformationClassNames>(
    'FeatureSetTransformation',
    featureSetTransformationStylesDefault
  );
  return (
    <div className={classNames.root} data-test="featureSetTransformation">
      <div className={classNames.fields}>
        <div className={classNames.fileUploadContainer}>
          <div className={classNames.fileUploadContainerHeader}>
            Select the{' '}
            {values.transformationType === TransformationType.TransformationMojo
              ? 'mojo transformation file(mojo)'
              : 'spark transformation pipeline file(zip)'}
          </div>
          <FileUpload
            uploadCallback={(files: File[]) => {
              if (files[0]) {
                const reader = new FileReader();
                reader.onloadend = (e) => {
                  const md5Checksum = getMd5Checksum(e);
                  if (md5Checksum) {
                    onChangeValues({
                      ...values,
                      md5Checksum,
                      file: files[0],
                    });
                  }
                };
                reader.readAsArrayBuffer(files[0]);
              } else {
                onChangeValues({
                  ...values,
                  md5Checksum: undefined,
                  file: undefined,
                });
              }
            }}
            maxFileSize={2e8}
            maxSize={2e8}
            fileExtensions={[
              ...(values.transformationType === TransformationType.TransformationMojo ? ['mojo'] : ['zip']),
            ]}
            data-test="upload-transformation-file-upload"
          />
        </div>
        <Dropdown
          label="Project Source"
          placeholder="Select Project"
          options={projectOptions || []}
          selectedKey={values.projectId}
          onChange={(_e, option) => {
            if (option) {
              loadFeatureSets(option.key as string);
            }
          }}
        />
        <Dropdown
          label="Feature Set Source"
          placeholder="Select Feature Set"
          options={featureSetOptions || []}
          selectedKey={values.featureSetIdVersion?.id}
          onChange={(_e, option) => {
            if (option) {
              const id = option.key as string;
              const featureSetIdVersion: VersionedId = { id, majorVersion: parseInt(option.data.version) };
              onChangeValues({ ...values, featureSetIdVersion });
            }
          }}
        />
      </div>
    </div>
  );
};
